import {
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Typography,
  ListItemIcon,
  menuItemClasses,
  useTheme
} from "@mui/material";
import React, { useState } from "react";
import Avatar from "@hyro-components/Avatar";
import {
  hasPermissionSelector,
  isAdminSelector,
  selectedAccountIdSelector,
  userDataSelector
} from "../../stores/selectors/appSelectors";
import CrownIcon from "../../icons/CrownIcon";
import ExternalLink from "src/icons/ExternalLink";
import useAppStore from "../../stores/appStore";
import { useIsAdminPanel, useIsMobile } from "@hooks";
import { logout } from "../../auth/frontegg";
import { useQueryClient } from "@tanstack/react-query";
import { AdminPortal } from "@frontegg/react";
import UsersIcon from "src/icons/UserIcon";
import LogoutIcon from "src/icons/LogoutIcon";
import HelpIcon from "src/icons/HelpIcon";
import DocsIcon from "src/icons/DocsIcon";
import { LINKS, ADMIN, ALL_ASSISTANTS } from "@consts";
import { useFlags } from "launchdarkly-react-client-sdk";

const UserIconButton = styled(IconButton)(() => ({
  "padding": 0,
  "&:active, &:hover": {
    outline: "2px solid #E0E0FF"
  }
}));

const StyledUserMenu = styled(props => (
  <Menu
    id="menu-appbar"
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right"
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right"
    }}
    {...props}
  />
))(({ theme }) => ({
  "marginTop": theme.spacing(1.5),
  "& .MuiButtonBase-root": {
    "padding": `${theme.spacing()}`,
    "gap": theme.spacing(1.5),
    "&:hover": {
      backgroundColor: theme.palette.system[50],
      borderRadius: theme.spacing()
    }
  }
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  justifyContent: "center"
}));

const UserMenu = () => {
  const isMobile = useIsMobile();
  const userData = useAppStore(userDataSelector);
  const userName = userData?.name || userData?.email;
  const isAdmin = useAppStore(isAdminSelector);
  const isAdminPanel = useIsAdminPanel();
  const [clientMenuAnchorEl, setClientMenuAnchorEl] = useState(null);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { showSupportLink, showDocs } = useFlags();
  const accountId = useAppStore(selectedAccountIdSelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const hasAdminPanelPermission = hasPermission("fe.secure.read.users");

  const toggleMenu = ({ currentTarget }) =>
    clientMenuAnchorEl
      ? setClientMenuAnchorEl(null)
      : setClientMenuAnchorEl(currentTarget);

  const handleMenuClose = () => {
    setClientMenuAnchorEl(null);
  };

  const handleUserManagement = () => {
    AdminPortal.show();
    handleMenuClose();
  };

  return (
    <>
      <UserIconButton
        sx={{ ml: .5 }}
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="false"
        color="inherit"
        onClick={toggleMenu}
        size="large"
        data-testid="user-menu-avatar-button"
      >
        <Avatar sx={{ fontWeight: 500 }} fontSize={11} size={isMobile ? 24 : 32} name={userName} />
      </UserIconButton>
      <StyledUserMenu
        anchorEl={clientMenuAnchorEl}
        elevation={3}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={Boolean(clientMenuAnchorEl)}
        onClose={toggleMenu}
      >
        <MenuItem disabled sx={{ [`&.${menuItemClasses.disabled}`]: { opacity: 1 } }}>
          <StyledListItemIcon>
            <Avatar sx={{ fontWeight: 500 }} fontSize={11} size={isMobile ? 24 : 32} name={userName} />
          </StyledListItemIcon>
          <Typography variant="subtitle2" color="text.800">{userName}</Typography>

        </MenuItem>
        {userData?.tenantIds?.includes(accountId) && hasAdminPanelPermission ? <MenuItem onClick={handleUserManagement} sx={{ marginTop: .5 }}>
          <StyledListItemIcon>
            <UsersIcon />
          </StyledListItemIcon>
          <ListItemText>
            <Typography variant="body2" color="text.800">
              User Management
            </Typography>
          </ListItemText>
        </MenuItem> : null}
        {[
          isAdmin && !isAdminPanel && {
            title: "Admin Panel",
            icon: <CrownIcon />,
            url: `${window.location.protocol}//${window.location.host}/${ADMIN}/${ALL_ASSISTANTS}`,
          },
          showDocs && {
            title: "Documentation",
            icon: <DocsIcon size={24} />,
            url: LINKS.HYRO_DOCS
          },
          showSupportLink && {
            title: "Hyro Support",
            icon: <HelpIcon size={24} />,
            url: LINKS.HYRO_SUPPORT
          },
        ].filter(Boolean).map((item) => (
          <MenuItem
            key={item.title}
            sx={{ marginTop: 1 }}
            onClick={() => {
              window.open(item.url);
              handleMenuClose();
            }}
          >
            <StyledListItemIcon>
              {item.icon}
            </StyledListItemIcon>
            <ListItemText>
              <Typography variant="body2" color="text.800">
                {item.title}
              </Typography>
            </ListItemText>
            <ListItemIcon sx={{ justifyContent: "end", pr: 1 }}>
              <ExternalLink />
            </ListItemIcon>
          </MenuItem>
        ))}

        <Divider />
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            localStorage.clear();
            queryClient.removeQueries();
            logout({ returnTo: window.location.origin });
          }}
        >
          <StyledListItemIcon>
            <LogoutIcon sx={{ color: theme.palette.system[700] }} />
          </StyledListItemIcon>
          <ListItemText>
            <Typography variant="body2" color="text.800">
              Log out
            </Typography>
          </ListItemText>
        </MenuItem>
      </StyledUserMenu >
    </>
  );
};

export default UserMenu;
